import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Separator } from "@/components/ui/separator";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { format } from "date-fns";
import { cn } from "@/lib/utils";
import { 
  Plus, Loader2, CheckCircle2, Clock, AlertCircle, 
  Wrench, FileText, Users, Cog, Shield, Eye, Calendar, User,
  GraduationCap, Search, MessageSquare, Target, BookOpen,
  ExternalLink, ArrowRight
} from "lucide-react";
import { 
  IncidentAction, ControlType, ActionStatus,
  CONTROL_TYPE_LABELS
} from "@/types/incident";

type DeliveryMethod = "supervisor_task" | "popup" | "quest" | "banner" | "micro_learning";
type SupervisorTaskCategory = "fix" | "coach" | "inspect";
type ActionType = "corrective" | "preventive";

interface Worker {
  id: string;
  display_name: string;
  role: string;
}

interface IncidentActionFormProps {
  incidentId: string;
  companyId: string;
  siteId?: string;
  incidentDescription?: string;
  actions: IncidentAction[];
  onRefresh: () => void;
}

const DELIVERY_METHODS: { id: DeliveryMethod; label: string; description: string; icon: React.ReactNode; redirect?: boolean }[] = [
  { id: "supervisor_task", label: "Supervisor Task", description: "Tugas manual untuk supervisor", icon: <User className="h-5 w-5" /> },
  { id: "popup", label: "Popup", description: "Nudge, reminder, atau recognition", icon: <MessageSquare className="h-5 w-5" />, redirect: true },
  { id: "quest", label: "Quest", description: "Survey, challenge, atau gamified task", icon: <Target className="h-5 w-5" />, redirect: true },
  { id: "banner", label: "Banner", description: "Pesan harian untuk semua pekerja", icon: <FileText className="h-5 w-5" />, redirect: true },
  { id: "micro_learning", label: "Mikro Learning", description: "Modul pembelajaran singkat", icon: <BookOpen className="h-5 w-5" />, redirect: true },
];

const SUPERVISOR_TASK_CATEGORIES: { id: SupervisorTaskCategory; label: string; description: string; icon: React.ReactNode }[] = [
  { id: "fix", label: "Fix (Perbaikan)", description: "Tindakan korektif fisik atau administratif", icon: <Wrench className="h-4 w-4" /> },
  { id: "coach", label: "Coach (Pembinaan)", description: "Coaching/mentoring pekerja terkait", icon: <GraduationCap className="h-4 w-4" /> },
  { id: "inspect", label: "Inspect (Inspeksi)", description: "Verifikasi kondisi atau observasi", icon: <Search className="h-4 w-4" /> },
];

const STATUS_ICONS: Record<ActionStatus, React.ReactNode> = {
  pending: <Clock className="h-4 w-4 text-amber-600" />,
  in_progress: <Loader2 className="h-4 w-4 text-blue-600 animate-spin" />,
  completed: <CheckCircle2 className="h-4 w-4 text-green-600" />,
  verified: <Shield className="h-4 w-4 text-primary" />,
};

const STATUS_LABELS: Record<ActionStatus, string> = {
  pending: "Menunggu",
  in_progress: "Berjalan",
  completed: "Selesai",
  verified: "Terverifikasi",
};

export function IncidentActionForm({ 
  incidentId, 
  companyId, 
  siteId,
  incidentDescription,
  actions, 
  onRefresh 
}: IncidentActionFormProps) {
  const { toast } = useToast();
  const navigate = useNavigate();
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [supervisors, setSupervisors] = useState<Worker[]>([]);
  const [allWorkers, setAllWorkers] = useState<Worker[]>([]);

  // Form state
  const [actionType, setActionType] = useState<ActionType>("corrective");
  const [controlType, setControlType] = useState<ControlType>("engineering");
  const [description, setDescription] = useState("");
  const [deliveryMethod, setDeliveryMethod] = useState<DeliveryMethod>("supervisor_task");
  const [supervisorTaskCategory, setSupervisorTaskCategory] = useState<SupervisorTaskCategory>("fix");
  const [ownerId, setOwnerId] = useState("");
  const [coacheeId, setCoacheeId] = useState("");
  const [coachingContext, setCoachingContext] = useState("");
  const [dueDate, setDueDate] = useState("");
  const [verificationMethod, setVerificationMethod] = useState("");

  useEffect(() => {
    loadWorkers();
  }, [companyId, siteId]);

  const loadWorkers = async () => {
    // Load supervisors
    let supervisorQuery = supabase
      .from("worker_profiles")
      .select("id, display_name, role")
      .eq("company_id", companyId)
      .in("role", ["supervisor", "admin", "site_admin"]);
    
    if (siteId) {
      supervisorQuery = supervisorQuery.eq("site_id", siteId);
    }

    const { data: supervisorData } = await supervisorQuery;
    setSupervisors((supervisorData || []) as Worker[]);

    // Load all workers for coachee selection
    let workerQuery = supabase
      .from("worker_profiles")
      .select("id, display_name, role")
      .eq("company_id", companyId);
    
    if (siteId) {
      workerQuery = workerQuery.eq("site_id", siteId);
    }

    const { data: workerData } = await workerQuery;
    setAllWorkers((workerData || []) as Worker[]);
  };

  const handleDeliveryMethodSelect = (method: DeliveryMethod) => {
    setDeliveryMethod(method);
    
    // If method requires redirect, navigate to appropriate wizard
    const methodConfig = DELIVERY_METHODS.find(m => m.id === method);
    if (methodConfig?.redirect) {
      // Build prefill state for intervention wizard
      const prefillData = {
        source: "incident_action",
        sourceId: incidentId,
        title: `[Tindakan Insiden] ${description || incidentDescription?.slice(0, 50) || "Aksi Korektif"}`,
        description: description || `Tindakan ${actionType === "corrective" ? "korektif" : "preventif"} dari investigasi insiden`,
        category: controlType,
        context: {
          incident_id: incidentId,
          action_type: actionType,
          control_type: controlType,
        },
      };

      if (method === "micro_learning") {
        navigate("/admin/create-learning", { state: { prefill: prefillData } });
      } else {
        navigate("/admin/create-intervention", { 
          state: { 
            prefill: {
              ...prefillData,
              deliveryMethod: method,
            }
          } 
        });
      }
      setIsDialogOpen(false);
    }
  };

  const handleSubmit = async () => {
    if (!description) {
      toast({ title: "Deskripsi wajib diisi", variant: "destructive" });
      return;
    }

    if (deliveryMethod === "supervisor_task") {
      if (!ownerId) {
        toast({ title: "Pilih supervisor penanggung jawab", variant: "destructive" });
        return;
      }
      if (supervisorTaskCategory === "coach" && !coacheeId) {
        toast({ title: "Pilih pekerja yang akan dibina", variant: "destructive" });
        return;
      }
    }

    setLoading(true);
    try {
      // Call edge function to create action with all related records
      const { data: result, error: fnError } = await supabase.functions.invoke("create-incident-actions", {
        body: {
          incident_id: incidentId,
          company_id: companyId,
          site_id: siteId,
          action_type: actionType,
          control_type: controlType,
          action_description: description,
          delivery_method: deliveryMethod,
          owner_id: deliveryMethod === "supervisor_task" ? ownerId : null,
          supervisor_task_category: deliveryMethod === "supervisor_task" ? supervisorTaskCategory : null,
          coachee_id: supervisorTaskCategory === "coach" ? coacheeId : null,
          coaching_context: supervisorTaskCategory === "coach" ? coachingContext : null,
          due_date: dueDate || null,
          verification_method: verificationMethod || null,
        },
      });

      if (fnError) throw fnError;
      if (result?.error) throw new Error(result.error);

      toast({ title: "Tindakan berhasil ditambahkan" });
      setIsDialogOpen(false);
      resetForm();
      onRefresh();
    } catch (error) {
      console.error("Error creating action:", error);
      toast({ title: "Gagal membuat tindakan", variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setActionType("corrective");
    setControlType("engineering");
    setDescription("");
    setDeliveryMethod("supervisor_task");
    setSupervisorTaskCategory("fix");
    setOwnerId("");
    setCoacheeId("");
    setCoachingContext("");
    setDueDate("");
    setVerificationMethod("");
  };

  const completedCount = actions.filter(a => a.status === "completed" || a.status === "verified").length;

  return (
    <div className="space-y-6">
      {/* Summary */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold">Tindakan Korektif/Preventif</h3>
          <p className="text-sm text-muted-foreground">
            {completedCount}/{actions.length} tindakan selesai
          </p>
        </div>
        <Button onClick={() => setIsDialogOpen(true)} className="gap-2">
          <Plus className="h-4 w-4" />
          Tambah Tindakan
        </Button>
      </div>

      {/* Progress Bar */}
      {actions.length > 0 && (
        <div className="space-y-2">
          <div className="h-2 bg-muted rounded-full overflow-hidden">
            <div 
              className="h-full bg-primary transition-all"
              style={{ width: `${(completedCount / actions.length) * 100}%` }}
            />
          </div>
        </div>
      )}

      {/* Action List */}
      {actions.length === 0 ? (
        <Card>
          <CardContent className="py-8 text-center text-muted-foreground">
            Belum ada tindakan. Klik "Tambah Tindakan" untuk memulai.
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {actions.map((action) => (
            <Card key={action.id} className={cn(
              "border-l-4",
              action.status === "verified" && "border-l-primary",
              action.status === "completed" && "border-l-success",
              action.status === "in_progress" && "border-l-blue-500",
              action.status === "pending" && "border-l-amber-500",
            )}>
              <CardContent className="p-4">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 space-y-2">
                    <div className="flex items-center gap-2 flex-wrap">
                      <Badge className={CONTROL_TYPE_LABELS[action.control_type]?.color || "bg-muted"}>
                        {CONTROL_TYPE_LABELS[action.control_type]?.label || action.control_type}
                      </Badge>
                      {(action as any).action_type && (
                        <Badge variant="outline">
                          {(action as any).action_type === "corrective" ? "Korektif" : "Preventif"}
                        </Badge>
                      )}
                      {(action as any).delivery_method && (
                        <Badge variant="secondary" className="text-xs">
                          via {(action as any).delivery_method}
                        </Badge>
                      )}
                    </div>
                    
                    <p className="font-medium">{action.action_description}</p>
                    
                    <div className="flex flex-wrap items-center gap-4 text-sm text-muted-foreground">
                      {action.owner && (
                        <span className="flex items-center gap-1">
                          <User className="h-3 w-3" />
                          {action.owner.display_name}
                        </span>
                      )}
                      {action.due_date && (
                        <span className="flex items-center gap-1">
                          <Calendar className="h-3 w-3" />
                          {format(new Date(action.due_date), "dd MMM yyyy")}
                        </span>
                      )}
                    </div>

                    {action.linked_supervisor_task_id && (
                      <Button variant="link" size="sm" className="p-0 h-auto text-xs">
                        <Eye className="h-3 w-3 mr-1" />
                        Lihat Tugas Supervisor
                      </Button>
                    )}
                  </div>

                  <div className="flex items-center gap-2">
                    {STATUS_ICONS[action.status]}
                    <span className="text-sm font-medium">
                      {STATUS_LABELS[action.status]}
                    </span>
                  </div>
                </div>

                {/* Evidence */}
                {action.evidence_photo_url && (
                  <div className="mt-3 pt-3 border-t">
                    <p className="text-xs text-muted-foreground mb-2">Bukti Penyelesaian:</p>
                    <img 
                      src={action.evidence_photo_url} 
                      alt="Evidence" 
                      className="h-20 w-20 object-cover rounded-lg border"
                    />
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Add Action Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Tambah Tindakan Baru</DialogTitle>
            <DialogDescription>
              Pilih metode delivery untuk menindaklanjuti insiden
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-6 py-4">
            {/* Action Type */}
            <div className="space-y-2">
              <Label>Jenis Tindakan</Label>
              <RadioGroup
                value={actionType}
                onValueChange={(v) => setActionType(v as ActionType)}
                className="flex gap-4"
              >
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="corrective" id="corrective" />
                  <Label htmlFor="corrective">Korektif</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="preventive" id="preventive" />
                  <Label htmlFor="preventive">Preventif</Label>
                </div>
              </RadioGroup>
            </div>

            {/* Control Type */}
            <div className="space-y-2">
              <Label>Jenis Pengendalian</Label>
              <div className="flex flex-wrap gap-2">
                {(Object.entries(CONTROL_TYPE_LABELS) as [ControlType, typeof CONTROL_TYPE_LABELS.engineering][]).map(([key, info]) => (
                  <Button
                    key={key}
                    variant={controlType === key ? "default" : "outline"}
                    size="sm"
                    onClick={() => setControlType(key)}
                  >
                    {info.label}
                  </Button>
                ))}
              </div>
            </div>

            {/* Description */}
            <div className="space-y-2">
              <Label>Deskripsi Tindakan *</Label>
              <Textarea
                placeholder="Jelaskan tindakan yang akan dilakukan..."
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                className="min-h-[80px]"
              />
            </div>

            <Separator />

            {/* Delivery Method */}
            <div className="space-y-3">
              <Label className="text-base font-semibold">Metode Delivery</Label>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {DELIVERY_METHODS.map((method) => (
                  <Card
                    key={method.id}
                    className={cn(
                      "cursor-pointer transition-all hover:shadow-md",
                      deliveryMethod === method.id 
                        ? "border-primary ring-2 ring-primary/20" 
                        : "hover:border-primary/50"
                    )}
                    onClick={() => handleDeliveryMethodSelect(method.id)}
                  >
                    <CardContent className="p-4">
                      <div className="flex items-start gap-3">
                        <div className={cn(
                          "p-2 rounded-lg",
                          deliveryMethod === method.id ? "bg-primary text-primary-foreground" : "bg-muted"
                        )}>
                          {method.icon}
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <p className="font-medium">{method.label}</p>
                            {method.redirect && (
                              <ExternalLink className="h-3 w-3 text-muted-foreground" />
                            )}
                          </div>
                          <p className="text-xs text-muted-foreground">{method.description}</p>
                          {method.redirect && (
                            <p className="text-xs text-primary mt-1 flex items-center gap-1">
                              <ArrowRight className="h-3 w-3" />
                              Buka wizard konfigurasi
                            </p>
                          )}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>

            {/* Supervisor Task Specific Options */}
            {deliveryMethod === "supervisor_task" && (
              <>
                <Separator />
                
                {/* Task Category */}
                <div className="space-y-3">
                  <Label className="font-semibold">Kategori Tugas Supervisor</Label>
                  <RadioGroup
                    value={supervisorTaskCategory}
                    onValueChange={(v) => setSupervisorTaskCategory(v as SupervisorTaskCategory)}
                    className="space-y-2"
                  >
                    {SUPERVISOR_TASK_CATEGORIES.map((cat) => (
                      <div 
                        key={cat.id}
                        className={cn(
                          "flex items-start space-x-3 p-3 rounded-lg border transition-colors",
                          supervisorTaskCategory === cat.id 
                            ? "border-primary bg-primary/5" 
                            : "hover:bg-muted/50"
                        )}
                      >
                        <RadioGroupItem value={cat.id} id={cat.id} className="mt-0.5" />
                        <div className="flex-1">
                          <Label htmlFor={cat.id} className="flex items-center gap-2 font-medium cursor-pointer">
                            {cat.icon}
                            {cat.label}
                          </Label>
                          <p className="text-sm text-muted-foreground">{cat.description}</p>
                        </div>
                      </div>
                    ))}
                  </RadioGroup>
                </div>

                {/* Coach-specific fields */}
                {supervisorTaskCategory === "coach" && (
                  <Card className="border-dashed bg-muted/30">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm flex items-center gap-2">
                        <GraduationCap className="h-4 w-4" />
                        Detail Pembinaan
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label>Pekerja yang Dibina (Coachee) *</Label>
                          <Select value={coacheeId} onValueChange={setCoacheeId}>
                            <SelectTrigger>
                              <SelectValue placeholder="Pilih pekerja..." />
                            </SelectTrigger>
                            <SelectContent>
                              {allWorkers.map((worker) => (
                                <SelectItem key={worker.id} value={worker.id}>
                                  {worker.display_name} ({worker.role})
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="space-y-2">
                          <Label>Supervisor Pembina (Coach) *</Label>
                          <Select value={ownerId} onValueChange={setOwnerId}>
                            <SelectTrigger>
                              <SelectValue placeholder="Pilih supervisor..." />
                            </SelectTrigger>
                            <SelectContent>
                              {supervisors.map((sup) => (
                                <SelectItem key={sup.id} value={sup.id}>
                                  {sup.display_name} ({sup.role})
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                      <div className="space-y-2">
                        <Label>Konteks/Fokus Pembinaan</Label>
                        <Textarea
                          placeholder="Jelaskan fokus pembinaan, perilaku yang perlu diperbaiki, atau kompetensi yang perlu dikembangkan..."
                          value={coachingContext}
                          onChange={(e) => setCoachingContext(e.target.value)}
                          className="min-h-[60px]"
                        />
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* Owner for non-coach tasks */}
                {supervisorTaskCategory !== "coach" && (
                  <div className="space-y-2">
                    <Label>Penanggung Jawab (Supervisor) *</Label>
                    <Select value={ownerId} onValueChange={setOwnerId}>
                      <SelectTrigger>
                        <SelectValue placeholder="Pilih supervisor..." />
                      </SelectTrigger>
                      <SelectContent>
                        {supervisors.map((sup) => (
                          <SelectItem key={sup.id} value={sup.id}>
                            {sup.display_name} ({sup.role})
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}

                {/* Due Date */}
                <div className="space-y-2">
                  <Label>Tenggat Waktu</Label>
                  <Input
                    type="date"
                    value={dueDate}
                    onChange={(e) => setDueDate(e.target.value)}
                  />
                </div>

                {/* Verification Method */}
                <div className="space-y-2">
                  <Label>Metode Verifikasi</Label>
                  <Input
                    placeholder="Bagaimana tindakan ini akan diverifikasi..."
                    value={verificationMethod}
                    onChange={(e) => setVerificationMethod(e.target.value)}
                  />
                </div>
              </>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setIsDialogOpen(false)}>
              Batal
            </Button>
            {deliveryMethod === "supervisor_task" && (
              <Button onClick={handleSubmit} disabled={loading} className="gap-2">
                {loading && <Loader2 className="h-4 w-4 animate-spin" />}
                Buat Tindakan
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
